/**
 * Where events come from.
 *
 * One file wires every producer, so the answer to "what does this app report?" is this list and
 * not a search across the codebase. Screens do not call `track` themselves unless the thing
 * being measured is a press that only that screen knows about.
 */

import type { AnyRouter } from '@tanstack/react-router';
import { native, onNative, NativeCallError } from '../platform/tauri.js';
import { isNative } from '../platform/index.js';
import { getSettings, onSettingsChange } from '../lib/settings.js';
import { setAnalyticsEnabled, startAnalytics, track } from './client.js';
import { startWebAnalytics } from './webAnalytics.js';

/**
 * Launches a game, and counts it when the launcher says no.
 *
 * A wrapper rather than a line in each screen's catch block, because the interesting number is a
 * property of launching and not of the screen the press came from. `check_can_launch` has six
 * ways to refuse — no pass, an expired one, a clock that moved, a pending update — and which one
 * it was is the difference between a billing problem and a patcher problem. Today nobody can
 * tell, because the screen has room for a sentence and then the sentence is gone.
 *
 * The success side is not tracked here: `game://running` fires when a process actually starts,
 * which is a stronger claim than this call having returned.
 */
export async function launchGame(gameId: string): Promise<void> {
  try {
    await native.gameLaunch(gameId);
  } catch (err) {
    track('launch_refused', {
      gameId,
      detail: err instanceof NativeCallError ? err.code : 'unknown',
    });
    throw err;
  }
}

/**
 * The route template for what is on screen.
 *
 * TanStack's `routeId` is already the template — `/games/$slug`, never `/games/dustlands` — so
 * the resolved id never has to be stripped out of a URL here. The shared allowlist is the
 * backstop for anything that does not match one.
 */
function currentRoute(router: AnyRouter): string | undefined {
  const matches = router.state.matches;
  const last = matches[matches.length - 1];
  return last?.routeId as string | undefined;
}

/**
 * Starts collection.
 *
 * Called once from `main.tsx`, after the session has been restored so the first page view is
 * attributed to whoever is signed in rather than arriving a moment before they are known.
 */
export function startTracking(router: AnyRouter): void {
  setAnalyticsEnabled(getSettings().shareUsageData);
  onSettingsChange(() => setAnalyticsEnabled(getSettings().shareUsageData));

  startAnalytics();
  startWebAnalytics();

  // One per app start. The server counts these to get sessions; nothing about it identifies the
  // device it came from.
  track('session_start', { route: currentRoute(router) });
  track('page_view', { route: currentRoute(router) });

  /**
   * A page view per navigation.
   *
   * Subscribed to `onResolved` rather than to every location change: a preload, a redirect and a
   * pending state all move the router without a screen ever having been looked at, and counting
   * those would make the busiest screen in the app whichever one redirects most.
   */
  let lastRoute: string | undefined = currentRoute(router);
  router.subscribe('onResolved', () => {
    const route = currentRoute(router);
    if (!route || route === lastRoute) return;
    lastRoute = route;
    track('page_view', { route });
  });

  if (isNative) wireNativeEvents();
}

/**
 * The desktop events.
 *
 * `game://exited` is the one that matters: the launcher has been measuring session length in
 * its process watcher since before any of this existed, and nobody was listening. Everything
 * here is a forward of something the app already knew.
 */
function wireNativeEvents(): void {
  void onNative('game://running', (event) => {
    track('game_launch', { gameId: event.gameId });
  });

  void onNative('game://exited', (event) => {
    // A session shorter than this is a crash on startup or a player who changed their mind, and
    // counting it as playtime makes the median lie. It is still worth recording as a launch,
    // which the event above already did.
    if (event.durationS < 5) return;
    track('game_session_end', {
      gameId: event.gameId,
      value: event.durationS,
      detail: event.exitCode === null || event.exitCode === 0 ? 'clean' : 'nonzero_exit',
    });
  });

  /**
   * Which job was which.
   *
   * A state event says a job finished but not what kind of job it was, and an install and an
   * update are worth telling apart: one is a new player, the other is an existing one being made
   * to wait. The progress events do carry the kind, and they always run before a job can finish,
   * so remembering it as they go is enough.
   */
  const jobKinds = new Map<string, string>();

  void onNative('job://progress', (event) => {
    jobKinds.set(event.jobId, event.kind);
  });

  void onNative('job://state', (event) => {
    const kind = jobKinds.get(event.jobId);
    switch (event.state) {
      case 'downloading':
        // The first byte, rather than the press: a queued job that never starts because the
        // player changed their mind is not an install that failed.
        if (kind !== 'update') track('install_started', { gameId: event.gameId });
        break;
      case 'installed':
        track(kind === 'update' ? 'update_completed' : 'install_completed', {
          gameId: event.gameId,
        });
        jobKinds.delete(event.jobId);
        break;
      case 'error':
        track('install_failed', {
          gameId: event.gameId,
          detail: event.error?.code ?? 'unknown',
        });
        jobKinds.delete(event.jobId);
        break;
      default:
        break;
    }
  });
}
